<template>
  <v-container>
    <h1>Управление зонами</h1>

    <v-btn color="primary" class="mb-4" @click="openZoneModal">Добавить зону</v-btn>

    <v-list two-line>
      <v-list-item v-for="zone in zones" :key="zone.id">
        <v-list-item-content>
          <v-list-item-title>{{ zone.name }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-btn color="red" text @click="deleteZone(zone.id)">Удалить</v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <v-dialog v-model="showModal" max-width="860px" persistent>
      <v-card>
        <v-card-title class="headline">Добавить зону</v-card-title>

        <v-card-text>
          <v-text-field
            v-model="zoneName"
            label="Название зоны"
            required
            class="mb-2"
          />

          <v-text-field
            v-model="searchQuery"
            label="Поиск адреса"
            append-icon="mdi-magnify"
            @click:append="searchAddress"
            @keyup.enter="searchAddress"
            class="mb-3"
          />

          <!-- важное: ref вместо id, чтобы гарантированно попасть в DOM -->
          <div ref="zoneMap" class="zone-map"></div>

          <div class="mt-2 grey--text text--darken-1" style="font-size:12px;">
            Подсказка: начните рисовать полигон кликами по карте, двойной клик — завершить.
            Узлы можно подтягивать. Разрешена только одна зона.
          </div>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn color="green" dark @click="saveZone">Сохранить</v-btn>
          <v-btn text @click="closeZoneModal">Отмена</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'

export default {
  name: 'AdminZones',
  data () {
    return {
      zones: [],
      showModal: false,
      zoneName: '',
      searchQuery: '',

      map: null,
      drawnItems: null,
      drawControl: null
    }
  },

  async mounted () {
    await this.loadZones()
  },

  watch: {
    // При открытии диалога создаём карту (один раз) и обязательно делаем invalidateSize
    showModal (val) {
      if (val) {
        this.$nextTick(() => {
          if (!this.map) this.initMap()
          // небольшой таймаут, чтобы анимация диалога завершилась
          setTimeout(() => this.map && this.map.invalidateSize(), 200)
        })
      }
    }
  },

  methods: {
    async loadZones () {
      const { data } = await axios.get('https://geonode.qpark.io/api/zones')
      this.zones = Array.isArray(data) ? data : []
    },

    openZoneModal () {
      this.showModal = true
    },

    closeZoneModal () {
      this.showModal = false
      // чистим форму
      this.zoneName = ''
      this.searchQuery = ''
      if (this.drawnItems) this.drawnItems.clearLayers()
    },

    initMap () {
      // базовая карта
      this.map = L.map(this.$refs.zoneMap, {
        center: [55.751244, 37.618423],
        zoom: 11,
        zoomControl: true
      })

      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.map)

      // слой нарисованных объектов
      this.drawnItems = new L.FeatureGroup()
      this.map.addLayer(this.drawnItems)

      // инструмент рисования (только Polygon)
      this.drawControl = new L.Control.Draw({
        position: 'topleft',
        edit: { featureGroup: this.drawnItems },
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: true,
            shapeOptions: { color: '#4285f4', weight: 2, fillOpacity: 0.25 }
          },
          polyline: false,
          rectangle: false,
          circle: false,
          marker: false,
          circlemarker: false
        }
      })
      this.map.addControl(this.drawControl)

      // Разрешаем только ОДИН полигон
      this.map.on(L.Draw.Event.CREATED, ({ layer }) => {
        this.drawnItems.clearLayers()
        this.drawnItems.addLayer(layer)
      })
    },

    async searchAddress () {
      if (!this.searchQuery) return
      try {
        const { data } = await axios.get(
          'https://geonode.qpark.io/api/search-address',
          { params: { q: this.searchQuery, format: 'json' } }
        )
        if (Array.isArray(data) && data.length) {
          const lat = parseFloat(data[0].lat)
          const lon = parseFloat(data[0].lon)
          if (this.map && Number.isFinite(lat) && Number.isFinite(lon)) {
            this.map.setView([lat, lon], 15) // ← ВАЖНО: для Leaflet setView, а не setCenter
          }
        } else {
          this.$toast ? this.$toast.error('Адрес не найден') : alert('Адрес не найден')
        }
      } catch (e) {
        console.error('searchAddress failed:', e)
        this.$toast ? this.$toast.error('Ошибка поиска адреса') : alert('Ошибка поиска адреса')
      }
    },

    async saveZone () {
      const layers = this.drawnItems ? this.drawnItems.getLayers() : []
      if (!this.zoneName || !layers.length) {
        this.$toast ? this.$toast.error('Введите название и нарисуйте зону') : alert('Введите название и нарисуйте зону')
        return
      }

      // берём единственный полигон
      const gj = layers[0].toGeoJSON()
      const coordinates = gj.geometry && gj.geometry.coordinates

      try {
        await axios.post('https://geonode.qpark.io/api/zones', {
          name: this.zoneName,
          coordinates
        })
        await this.loadZones()
        this.closeZoneModal()
      } catch (e) {
        console.error('saveZone failed:', e)
        this.$toast ? this.$toast.error('Не удалось сохранить зону') : alert('Не удалось сохранить зону')
      }
    },

    async deleteZone (id) {
      try {
        await axios.delete(`https://geonode.qpark.io/api/zones/${id}`)
        await this.loadZones()
      } catch (e) {
        console.error('deleteZone failed:', e)
        this.$toast ? this.$toast.error('Не удалось удалить зону') : alert('Не удалось удалить зону')
      }
    }
  }
}
</script>

<style scoped>
.zone-map {
  height: 420px;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #f7f7f7;
}
</style>
